import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { NodeIcon } from "@/components/NodeIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { headline, heroDate } from "@/lib/results";
import { fmtByDate } from "@/lib/months";
import { useBase } from "@/hooks/world";
import { fmtValue, pct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";
import type { ModelNode, MetricNode } from "@/model/types";

type Kind = "event" | "metric";

/**
 * A searchable node combobox: type to filter, arrows to move, Enter to choose. Grouped into Events and
 * Metrics, with each node's headline number for recognition.
 */
export function NodePicker({
	value,
	onChange,
	exclude,
	kinds,
	"nodes": given,
	filter,
	placeholder = "Choose…",
	autoFocus,
	className,
	"aria-label": ariaLabel,
}: {
	"value": string | undefined;
	"onChange": (id: string) => void;
	"exclude"?: string[];
	"kinds"?: Kind[];
	/** The nodes to choose from (defaults to every node in the worldview). */
	"nodes"?: ModelNode[];
	"filter"?: (n: ModelNode) => boolean;
	"placeholder"?: string;
	"autoFocus"?: boolean;
	"className"?: string;
	"aria-label"?: string;
}) {
	const all = useStore((s) => s.world.nodes);
	const [open, setOpen] = useState(false);
	const [q, setQ] = useState("");
	const triggerRef = useRef<HTMLButtonElement>(null);
	// Keys typed on the trigger before the search box takes focus are kept, not replaced.
	const typedWhileOpening = useRef(false);
	const pool = useMemo(
		() => (given ?? Object.values(all)).filter((n) => !exclude?.includes(n.id) && (!kinds || kinds.includes(n.kind)) && (!filter || filter(n))),
		[given, all, exclude, kinds, filter],
	);
	const selected = value ? (pool.find((n) => n.id === value) ?? given?.find((n) => n.id === value) ?? all[value]) : undefined;

	useEffect(() => {
		if (autoFocus) requestAnimationFrame(() => triggerRef.current?.focus());
	}, [autoFocus]);

	return (
		<Popover
			modal
			open={open}
			onOpenChange={(v) => {
				setOpen(v);
				if (!v) {
					setQ("");
					typedWhileOpening.current = false;
				}
			}}
		>
			<PopoverTrigger asChild>
				<button
					ref={triggerRef}
					type="button"
					role="combobox"
					aria-expanded={open}
					aria-haspopup="listbox"
					aria-label={ariaLabel ?? placeholder}
					className={cn(
						"flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-input px-2.5 text-left text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
						className,
					)}
					onKeyDown={(e) => {
						// Typing on the closed picker opens it with that letter already in the search box.
						if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && e.key !== " ") {
							e.preventDefault();
							const key = e.key;
							const append = typedWhileOpening.current;
							typedWhileOpening.current = true;
							setQ((prev) => (append ? prev + key : key));
							setOpen(true);
						} else if (e.key === "ArrowDown") {
							e.preventDefault();
							setOpen(true);
						}
					}}
				>
					{selected ? (
						<>
							<NodeIcon node={selected} className="text-muted-foreground" />
							<span className="min-w-0 flex-1 truncate">{selected.title}</span>
						</>
					) : (
						<span className="min-w-0 flex-1 truncate text-muted-foreground">{placeholder}</span>
					)}
					<ChevronDown className="size-4 shrink-0 text-muted-foreground" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-(--radix-popover-trigger-width) min-w-72 max-w-[calc(100vw-24px)] gap-0 p-0"
				align="start"
				collisionPadding={12}
				onCloseAutoFocus={(e) => {
					// If choosing moved focus on (e.g. to the next field), leave it there.
					const a = document.activeElement;
					if (a && a !== document.body && a !== triggerRef.current) e.preventDefault();
				}}
			>
				<PickerList
					pool={pool}
					value={value}
					q={q}
					setQ={setQ}
					onPick={(id) => {
						onChange(id);
						setOpen(false);
						setQ("");
						typedWhileOpening.current = false;
					}}
				/>
			</PopoverContent>
		</Popover>
	);
}

function PickerList({
	pool,
	value,
	q,
	setQ,
	onPick,
}: {
	pool: ModelNode[];
	value: string | undefined;
	q: string;
	setQ: (s: string) => void;
	onPick: (id: string) => void;
}) {
	const base = useBase();
	const known = useStore((s) => s.world.nodes);
	const listId = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const matches = useMemo(() => {
		const words = q.toLowerCase().split(/\s+/).filter(Boolean);
		const hit = (n: ModelNode) => {
			const hay = `${n.title} ${n.id}`.toLowerCase();
			return words.every((w) => hay.includes(w));
		};
		const rank = (n: ModelNode) => (words.length && n.title.toLowerCase().startsWith(words[0]) ? 0 : 1);
		const list = pool.filter(hit);
		const events = list.filter((n) => n.kind === "event").sort((a, b) => rank(a) - rank(b));
		const metrics = list.filter((n) => n.kind === "metric").sort((a, b) => rank(a) - rank(b));
		return { events, metrics, flat: [...events, ...metrics] };
	}, [pool, q]);

	const [active, setActive] = useState(() =>
		Math.max(
			0,
			matches.flat.findIndex((n) => n.id === value),
		),
	);
	useEffect(() => {
		// After the popover's focus scope has mounted (focus set earlier is pulled back by a surrounding dialog).
		const r = requestAnimationFrame(() => {
			const el = inputRef.current;
			if (!el) return;
			if (document.activeElement !== el) el.focus();
			// The focus scope selects the text; keep what was typed and put the caret after it.
			el.setSelectionRange(el.value.length, el.value.length);
		});
		return () => cancelAnimationFrame(r);
	}, []);
	useEffect(() => {
		if (active >= matches.flat.length) setActive(0);
	}, [matches.flat.length, active]);
	useEffect(() => {
		const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
		el?.scrollIntoView({ block: "nearest" });
	}, [active]);

	function number(n: ModelNode): string | null {
		if (!base.data || !known[n.id]) return null;
		const d = heroDate(n);
		const v = headline(n, base.data, d);
		if (v === null) return null;
		return `${n.kind === "event" ? pct(v) : fmtValue(v, (n as MetricNode).unit)} by ${fmtByDate(d)}`;
	}

	const optionId = (i: number) => `${listId}-o${i}`;
	let i = -1;
	const group = (label: string, items: ModelNode[]) =>
		items.length > 0 && (
			<div role="group" aria-label={label}>
				<div className="px-3 pt-2 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{label}</div>
				{items.map((n) => {
					const k = ++i;
					const num = number(n);
					return (
						<div
							key={n.id}
							id={optionId(k)}
							data-index={k}
							role="option"
							aria-selected={n.id === value}
							className={cn("flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm pointer-coarse:py-2.5", k === active && "bg-accent")}
							onMouseMove={() => k !== active && setActive(k)}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => onPick(n.id)}
						>
							<NodeIcon node={n} className="text-muted-foreground" />
							<span className="min-w-0 flex-1 truncate">{n.title}</span>
							{num && <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{num}</span>}
							<Check className={cn("size-3.5 shrink-0", n.id === value ? "opacity-100" : "opacity-0")} />
						</div>
					);
				})}
			</div>
		);

	return (
		<div>
			<div className="flex items-center gap-2 border-b px-3">
				<Search className="size-4 shrink-0 text-muted-foreground" />
				<input
					ref={inputRef}
					value={q}
					role="combobox"
					aria-expanded
					aria-controls={listId}
					aria-activedescendant={matches.flat.length ? optionId(active) : undefined}
					aria-label="Search nodes"
					placeholder="Search…"
					className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
					onChange={(e) => {
						setQ(e.target.value);
						setActive(0);
					}}
					onKeyDown={(e) => {
						const n = matches.flat.length;
						if (e.key === "ArrowDown" && n) {
							e.preventDefault();
							setActive((active + 1) % n);
						} else if (e.key === "ArrowUp" && n) {
							e.preventDefault();
							setActive((active - 1 + n) % n);
						} else if (e.key === "Home" && n) {
							e.preventDefault();
							setActive(0);
						} else if (e.key === "End" && n) {
							e.preventDefault();
							setActive(n - 1);
						} else if (e.key === "Enter") {
							e.preventDefault();
							const pick = matches.flat[active];
							if (pick) onPick(pick.id);
						}
					}}
				/>
			</div>
			<div ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto overscroll-contain py-1">
				{group("Events", matches.events)}
				{group("Metrics", matches.metrics)}
				{matches.flat.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted-foreground">Nothing matches “{q}”.</p>}
			</div>
		</div>
	);
}
